// Shape returned by GET /api/v1/tenants/me — keep in sync with backend
// CurrentTenantResponse.java. Flat-ish on purpose so simple checks like
// `tenant.subscriptionTier === 'PRO'` work without unwrapping nested objects.

export type SubscriptionTier = 'STARTER' | 'PRO' | 'BUSINESS';
export type SubscriptionStatus = 'TRIAL' | 'ACTIVE' | 'EXPIRED' | 'CANCELLED';

export interface ResourceQuota {
    used: number;
    limit: number;        // 1_000_000 sentinel = effectively unlimited
    unlimited: boolean;
}

export interface QuotaState {
    month: string;        // 'YYYY-MM'
    receiptOcr: ResourceQuota;
    orderParse: ResourceQuota;
    monthlyOrders: ResourceQuota;
}

export interface SeatLimit {
    current: number;
    included: number | null;       // included in plan (Pro = 5); null = unlimited
    hardCap: number | null;        // hard ceiling (Starter warehouses = 2); null = no cap
    overageRatePerSeat?: number;   // CAD per seat per month past included
}

export interface TierLimits {
    users: SeatLimit;
    warehouses: SeatLimit;
}

export interface CurrentTenant {
    tenantId: number;
    tenantName: string;
    subscriptionTier: SubscriptionTier;
    subscriptionStatus: SubscriptionStatus;
    subscriptionExpiresAt: string | null;
    trialEndsAt: string | null;
    subscriptionCouponCode: string | null;
    /** Pre-selected warehouse for QuickOrder / scan flows. Null = no preference. */
    defaultWarehouseId: number | null;
    quota: QuotaState | null;
    limits: TierLimits;
}

// Tier ordering: STARTER < PRO < BUSINESS. Keep this in sync with the
// backend enum's declaration order (covers() relies on ordinal).
const TIER_RANK: Record<SubscriptionTier, number> = {
    STARTER: 0,
    PRO: 1,
    BUSINESS: 2,
};

export function tierCovers(actual: SubscriptionTier | undefined, required: SubscriptionTier): boolean {
    if (!actual) return false;
    return TIER_RANK[actual] >= TIER_RANK[required];
}
